import { defineMenu, MENU_CLOSE_EVENT, type MenuElement, type MenuView } from 'BomdisoftComponents/menu/element';
import { delegateTooltips } from 'BomdisoftComponents/tooltip/delegate';

type FacebookShareResponse = { error_message?: string } | null | undefined;

type Facebook = { ui: (params: { display: 'popup'; href: string; method: 'share' }, callback: (response: FacebookShareResponse) => void) => void };

const isFacebook = (value: unknown): value is Facebook => typeof value === 'object' && value !== null && typeof Reflect.get(value, 'ui') === 'function';

export const readFacebook = (view: Window): Facebook | undefined => {
  const facebook: unknown = Reflect.get(view, 'FB');
  return isFacebook(facebook) ? facebook : undefined;
};

export const toXShareUrl = (message: string) => `https://x.com/intent/tweet?text=${encodeURIComponent(message)}`;

export type SocialMediaShareBehaviour = { onFailed?: () => void; onShared?: () => void; shareMessage: (href: string) => string };

export const delegateSocialMediaShare = (root: HTMLElement, { onFailed, onShared, shareMessage }: SocialMediaShareBehaviour) => {
  const view = root.ownerDocument.defaultView as MenuView | null;
  const trigger = root.querySelector<HTMLButtonElement>('.bomdisoft-social-share-trigger');
  const menuId = trigger?.getAttribute('aria-controls') ?? null;
  const menu = menuId === null ? null : (root.ownerDocument.getElementById(menuId) as MenuElement | null);
  if (view === null || trigger === null || menu === null) return () => {};
  defineMenu(view);
  const tooltipRoot = root.getRootNode();
  if (tooltipRoot.nodeType === tooltipRoot.DOCUMENT_NODE || tooltipRoot.nodeType === tooltipRoot.DOCUMENT_FRAGMENT_NODE) delegateTooltips(tooltipRoot as Document | ShadowRoot);
  menu.anchorRef = { current: trigger };

  const handleTrigger = () => {
    menu.open = true;
  };

  const handleMenu = (event: Event) => {
    const target = event.target instanceof view.Element ? event.target : null;
    const row = target?.closest('[data-testid="share-facebook"], [data-testid="share-x"]') ?? null;
    if (row === null) return;
    menu.open = false;
    if (row.getAttribute('data-testid') === 'share-x') {
      view.open(toXShareUrl(shareMessage(view.location.href)), '_blank', 'noopener,noreferrer');
      return;
    }
    readFacebook(view)?.ui({ display: 'popup', href: view.location.href, method: 'share' }, response => {
      if (response && !response.error_message) onShared?.();
      else onFailed?.();
    });
  };

  const handleClosed = () => {
    menu.open = false;
  };

  trigger.addEventListener('click', handleTrigger);
  menu.addEventListener('click', handleMenu);
  menu.addEventListener(MENU_CLOSE_EVENT, handleClosed);
  return () => {
    trigger.removeEventListener('click', handleTrigger);
    menu.removeEventListener('click', handleMenu);
    menu.removeEventListener(MENU_CLOSE_EVENT, handleClosed);
  };
};
